import { trackStandardsApplied } from '@/lib/analytics';
import { getFieldsWithSuggestions } from '@/modules/templates/lib/dynamic-flow/utils';
import { AiMagicIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';

import { Button } from '@/components/ui-kit/button';
import { useTranslation } from 'react-i18next';
import { ScreenWithFields, useDynamicForm } from './DynamicFormContext';
import { DynamicFieldType } from './hooks/useDynamicFields';
import { useMediaQuery } from '@/hooks/use-media-query';

interface StandardScreenBannerProps {
  variant: 'standard';
  currentScreen: ScreenWithFields;
  appliedStandard: boolean;
  applyingStandard: boolean;
  onApply: () => void;
}

interface DynamicScreenBannerProps {
  variant: 'dynamic';
  currentScreen: ScreenWithFields;
  fields: DynamicFieldType[];
  jurisdictionName: string | null;
  appliedStandard: boolean;
  applyingStandard: boolean;
  onApply: () => void;
}

type ApplyStandardsBannerProps = StandardScreenBannerProps | DynamicScreenBannerProps;

export default function ApplyStandardsBanner(props: ApplyStandardsBannerProps) {
  const { t } = useTranslation();
  const { config, formData, enrichmentContext, getVisibleFields } = useDynamicForm();
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (props.variant === 'dynamic') {
    return (
      <DynamicBanner
        fields={props.fields}
        jurisdictionName={props.jurisdictionName}
        appliedStandard={props.appliedStandard}
        applyingStandard={props.applyingStandard}
        onApply={props.onApply}
        formData={formData}
        configSlug={config?.slug}
      />
    );
  }

  // Standard screen
  const { currentScreen, appliedStandard, applyingStandard, onApply } = props;

  if (!(currentScreen as ScreenWithFields)?.enableApplyStandards) return null;

  const visibleFields = getVisibleFields(currentScreen) || [];
  const fieldsWithSuggestions = getFieldsWithSuggestions(visibleFields, enrichmentContext);
  const unfilledFieldsWithSuggestions = fieldsWithSuggestions.filter(
    (f) => !formData[f.name] || formData[f.name] === ''
  );

  if (unfilledFieldsWithSuggestions.length > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-[#ECF7FF] p-4 mb-6 font-nunito"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fff]">
              <HugeiconsIcon icon={AiMagicIcon} className="w-6 h-6 text-selise-blue-500" />
            </div>
            <div>
              <p className="font-semibold text-primary-light">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_SMARTTITLE')}
              </p>
              <p className="text-sm text-secondary-400">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_SMARTAUTOFILL', {
                  count: unfilledFieldsWithSuggestions.length,
                })}
              </p>
            </div>
          </div>
          <div>
            <Button
              size={isMobile ? 'sm' : 'lg'}
              variant="primary"
              onClick={() => {
                if (config) {
                  const unfilledCount = getFieldsWithSuggestions(
                    getVisibleFields(currentScreen) || [],
                    enrichmentContext
                  ).filter((f) => !formData[f.name] || formData[f.name] === '').length;
                  if (unfilledCount > 0) {
                    trackStandardsApplied(config.slug, unfilledCount);
                  }
                }
                onApply();
              }}
              disabled={applyingStandard}
              className="flex items-center gap-2 px-5 shadow-md hover:shadow-lg transition-all font-medium *:text-white! [&_svg]:text-white!"
              style={{ color: 'white' }}
            >
              <Zap className="h-4 w-4" />
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_APPLYSTANDARDS')}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  if (appliedStandard && fieldsWithSuggestions.length > 0) {
    return (
      <AppliedSuccessBanner
        label={t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_SMARTAPPLIEDTITLE')}
      />
    );
  }

  return null;
}

// --- Internal sub-components ---

function DynamicBanner({
  fields,
  jurisdictionName,
  appliedStandard,
  applyingStandard,
  onApply,
  formData,
  configSlug,
}: {
  fields: DynamicFieldType[];
  jurisdictionName: string | null;
  appliedStandard: boolean;
  applyingStandard: boolean;
  onApply: () => void;
  formData: Record<string, unknown>;
  configSlug: string | undefined;
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const unfilledFieldsWithStandards = fields.filter(
    (f) => f.standardValue && (!formData[f.name] || formData[f.name] === '')
  );

  if (unfilledFieldsWithStandards.length > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-selise-blue-500/10 bg-selise-blue-500/10 p-4 mb-6 font-nunito"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--white))]/80">
              <HugeiconsIcon icon={AiMagicIcon} className="w-6 h-6 text-selise-blue-500" />
            </div>
            <div>
              <p className="font-semibold text-[hsl(var(--fg))]">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_LOCALTITLE', {
                  jurisdiction:
                    jurisdictionName || t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_LOCALFALLBACK'),
                })}
              </p>
              <p className="text-sm text-globe-grey">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_LOCALAUTOFILL', {
                  count: unfilledFieldsWithStandards.length,
                })}
              </p>
            </div>
          </div>
          <div>
            <Button
              size={isMobile ? 'sm' : 'lg'}
              onClick={() => {
                if (configSlug) {
                  const unfilledCount = fields.filter(
                    (f) => f.standardValue && (!formData[f.name] || formData[f.name] === '')
                  ).length;
                  if (unfilledCount > 0) {
                    trackStandardsApplied(configSlug, unfilledCount);
                  }
                }
                onApply();
              }}
              variant="primary"
              disabled={applyingStandard}
              className="flex items-center gap-2 px-5 shadow-md hover:shadow-lg transition-all font-medium"
              style={{ color: 'white' }}
            >
              <Zap className="h-4 w-4" />
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_NAVIGATION_APPLYSTANDARDS')}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  if (appliedStandard && fields.some((f) => f.standardValue)) {
    return (
      <AppliedSuccessBanner
        label={t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_LOCALAPPLIEDTITLE', {
          jurisdiction:
            jurisdictionName || t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_LOCALFALLBACK'),
        })}
      />
    );
  }

  return null;
}

function AppliedSuccessBanner({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-6"
    >
      <div className="flex items-center gap-3 font-nunito">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-200">
          <Check className="h-5 w-5 text-emerald-700" />
        </div>
        <div>
          <p className="font-semibold text-emerald-800">{label}</p>
          <p className="text-sm text-emerald-600">
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_STANDARDS_APPLIEDDESCRIPTION')}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
